import { Graphics } from '@inlet/react-pixi';
import { useMemo } from 'react';

import { OptionT } from 'components/powerFlowShared/components/types';
import { useFindQueue } from 'components/powerFlowShared/hooks/useFindQueue';
import { usePixiBadge } from 'components/powerFlowShared/hooks/usePixiBadge';
import { flowNodeColor } from 'fable/flowEditor';
import { PhoneExtraData } from 'fable/flowEditor/components/flowManager/canvas/types/extraData';
import { Text } from 'flow-builder/node/text';
import { TextContent } from 'flow-builder/node/textContent';
import { TextEllipsis } from 'flow-builder/node/textEllipsis';
import { hexStringToNumber } from 'flow-builder/renderers/common/colors';
import type {
  GenericNodeAttributes,
  GenericNodeProps,
} from 'flow-builder/types/node';

import { VoicemailModal } from '../../modals/voicemail';
import type { AnnouncementPayloadT } from '../../types';

import { useAnnouncementName } from './callback';

export type Payload = {
  announcement: AnnouncementPayloadT;
  queue: null | OptionT;
  transcription: boolean;
};

type VoicemailNodeAttributes = GenericNodeAttributes<Payload, PhoneExtraData>;
type VoicemailNodeProps = GenericNodeProps<Payload, PhoneExtraData>;

const updateConnection: VoicemailNodeAttributes['updateConnection'] = (
  node,
  _connection,
  connectionId,
) => {
  const payload = { ...node.payload };

  if (payload?.queue) {
    payload.queue = {
      ...payload.queue,
      value: connectionId,
    };
  }

  return { ...node, payload };
};

export const VoicemailNodeAttributes: VoicemailNodeAttributes = {
  iconName: 'conversations',
  label: 'Voicemail',
  helpText:
    'Record or upload a voicemail asking your customers to leave a message if no agents are available.',
  color: hexStringToNumber(flowNodeColor.green),
  translatePayload: () => [],
  updateConnection,
  NodeContent: VoicemailNode,
  ActionModal: VoicemailModal,
};

export function VoicemailNode({
  node,
  context,
}: VoicemailNodeProps): JSX.Element {
  const { extraData } = context;
  const { payload } = node;

  const announcement = useAnnouncementName(payload.announcement, extraData);
  const queue = useFindQueue(payload.queue, extraData);

  const drawArchivedBadge = usePixiBadge({
    text: 'Archived',
    variant: 'error',
    offset: { x: 0, y: 52 },
  });

  return (
    <TextContent>
      {useMemo(
        () => (
          <>
            <TextEllipsis>
              <Text weight="normal" text="Announcement" />
              <Text weight="semibold" text={announcement} />
            </TextEllipsis>

            <TextEllipsis offset={{ y: 5, x: 0 }}>
              <Text weight="normal" text="Queue" />
              <Text
                weight="semibold"
                text={
                  queue?.name ?? payload.queue?.label ?? 'No queue selected'
                }
              />
            </TextEllipsis>

            {queue?.is_archived && <Graphics draw={drawArchivedBadge} />}
          </>
        ),
        [announcement, drawArchivedBadge, payload.queue, queue],
      )}
    </TextContent>
  );
}
